import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, FileText, UserCircle, Package, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#5A7D6C';

const TrashCenterPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    institutions: [],
    patients: [],
    reports: [],
    therapists: [],
    resources: [],
  });

  const safeGet = async (url) => {
    try {
      const res = await axios.get(url, { withCredentials: true });
      return res.data || [];
    } catch (error) {
      return [];
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [institutions, patients, reports, therapists, resources] = await Promise.all([
      isSuperAdmin ? safeGet(`${API}/admin/institutions/trash`) : Promise.resolve([]),
      safeGet(`${API}/patients/trash`),
      safeGet(`${API}/reports/trash`),
      safeGet(`${API}/therapists/trash`),
      safeGet(`${API}/resources/trash`),
    ]);
    setData({ institutions, patients, reports, therapists, resources });
    setLoading(false);
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const restore = async (type, id) => {
    const url = type === 'institutions'
      ? `${API}/admin/institutions/${id}/restore`
      : `${API}/${type}/${id}/restore`;
    try {
      await axios.post(url, {}, { withCredentials: true });
      toast.success('Elemento restaurado');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  const purge = async (type, id, name) => {
    if (type === 'institutions') {
      const typed = window.prompt(`Escribe el nombre exacto de la institución para eliminarla DEFINITIVAMENTE:\n\n"${name}"`);
      if (typed === null) return;
      try {
        await axios.delete(`${API}/admin/institutions/${id}/purge`, {
          withCredentials: true,
          data: { confirm_name: typed },
        });
        toast.success('Institución eliminada permanentemente');
        fetchAll();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al eliminar');
      }
      return;
    }
    if (!window.confirm('¿Eliminar este elemento DEFINITIVAMENTE? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/${type}/${id}/purge`, { withCredentials: true });
      toast.success('Elemento eliminado permanentemente');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const sections = [
    {
      key: 'institutions',
      title: 'Instituciones',
      icon: Building2,
      show: isSuperAdmin,
      items: data.institutions,
      label: (i) => i.name,
      sub: (i) => `${i.user_count ?? 0} usuarios`,
    },
    {
      key: 'patients',
      title: 'Pacientes',
      icon: Users,
      show: true,
      items: data.patients,
      label: (p) => p.name,
      sub: () => null,
    },
    {
      key: 'reports',
      title: 'Informes',
      icon: FileText,
      show: true,
      items: data.reports,
      label: (r) => `Sesión del ${r.session_date || 's/f'}`,
      sub: (r) => (r.objective ? r.objective.slice(0, 60) : null),
    },
    {
      key: 'therapists',
      title: 'Terapeutas',
      icon: UserCircle,
      show: true,
      items: data.therapists,
      label: (t) => t.name,
      sub: (t) => t.specialization || t.email || null,
    },
    {
      key: 'resources',
      title: 'Recursos',
      icon: Package,
      show: true,
      items: data.resources,
      label: (r) => r.name,
      sub: (r) => r.type || null,
    },
  ].filter((s) => s.show);

  const totalItems = sections.reduce((acc, s) => acc + s.items.length, 0);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><p>Cargando papelera...</p></div>;
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="trash-center-page">
      <div className="flex items-center gap-3 mb-2">
        <Trash2 className="h-8 w-8 text-amber-600" />
        <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#2D3748' }}>
          Papelera Central
        </h1>
      </div>
      <p className="text-sm text-[#718096] mb-6">
        Todo lo que envías a la papelera se conserva 30 días y luego se elimina automáticamente. Aquí puedes
        restaurarlo o eliminarlo definitivamente.
      </p>

      {totalItems === 0 && (
        <Card className="p-10 text-center" data-testid="trash-center-empty">
          <Trash2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">La papelera está vacía. Todo está en su sitio.</p>
        </Card>
      )}

      <div className="space-y-6">
        {sections.map((section) => {
          const Icon = section.icon;
          if (section.items.length === 0) return null;
          return (
            <Card key={section.key} className="p-6 border-amber-200" data-testid={`trash-section-${section.key}`}>
              <div className="flex items-center gap-2 mb-4">
                <Icon className="h-5 w-5" style={{ color: GREEN }} />
                <h2 className="text-lg font-semibold" style={{ color: '#2D3748' }}>{section.title}</h2>
                <Badge className="bg-amber-100 text-amber-800">{section.items.length}</Badge>
              </div>
              <div className="space-y-2">
                {section.items.map((item) => {
                  const days = item.days_remaining ?? 30;
                  const sub = section.sub(item);
                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between border rounded-lg px-4 py-3"
                      data-testid={`trash-item-${section.key}-${item.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate" style={{ color: '#2D3748' }}>{section.label(item)}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {sub && <span className="text-xs text-gray-500 truncate">{sub}</span>}
                          <Badge className={days <= 5 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                            {days} días restantes
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          onClick={() => restore(section.key, item.id)}
                          data-testid={`trash-restore-${section.key}-${item.id}`}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Restaurar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => purge(section.key, item.id, section.label(item))}
                          data-testid={`trash-purge-${section.key}-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TrashCenterPage;
